"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.flowPlanTooLarge = exports.FLOW_PLAN_TOO_LARGE_TEXT = exports.FLOW_PLAN_TOO_LARGE = exports.FLOW_PLAN_FILE_MAX_BYTES = exports.FLOW_PLAN_MAX_CHARS = void 0;
exports.remapFlowPlan = remapFlowPlan;
exports.validatePlanningDocument = validatePlanningDocument;
exports.compileFlowDocument = compileFlowDocument;
/** One plan, shared view/state references, no copied UI or executable metadata. */
const database_identity_1 = require("@streamlain/database-identity");
const app_base_binding_route_1 = require("./app-base-binding-route");
const app_flow_check_1 = require("./app-flow-check");
const app_flow_lifecycle_1 = require("./app-flow-lifecycle");
const app_flow_types_1 = require("./app-flow-types");
const idPattern = /^[a-zA-Z0-9][a-zA-Z0-9._-]{0,95}$/;
const sha256Pattern = /^[a-f0-9]{64}$/;
/** `value` is one of `allowed`, whatever else it may be. */
const oneOf = (value, allowed) => allowed.some(candidate => candidate === value);
const isId = (value) => typeof value === 'string' && idPattern.test(value);
const isDate = (value) => typeof value === 'string' && Number.isFinite(Date.parse(value));
function list(value, max, label) {
    if (!Array.isArray(value) || value.length > max)
        (0, app_flow_check_1.fail)(`invalid ${label}`);
}
/** The ids of `items`, each a valid id of its own; the id of a duplicate or an item without one is refused. */
const unique = (items, label) => {
    const ids = new Set();
    for (const item of items) {
        if (!item ||
            typeof item !== 'object' ||
            !('id' in item) ||
            typeof item.id !== 'string' ||
            !idPattern.test(item.id) ||
            ids.has(item.id))
            (0, app_flow_check_1.fail)(`duplicate or invalid ${label} id`);
        ids.add(item.id);
    }
    return ids;
};
const css = (value) => (0, app_flow_check_1.text)(value, 512) && !/[\u0000-\u001f]/.test(value);
/**
 * The largest plan an App keeps, as the characters of its JSON: the limit of
 * the cell of the App's settings that holds it (a JSON column is a rich-text
 * column), so a plan that fits is a plan the platform can store.
 */
exports.FLOW_PLAN_MAX_CHARS = database_identity_1.DATABASE_LARGE_DATA_POLICY.cell.limits['rich-text'].limit;
/**
 * The largest plan file, in bytes: a file is indented and may hold letters of
 * several bytes, so it is larger than the plan it holds.
 */
exports.FLOW_PLAN_FILE_MAX_BYTES = 4 * exports.FLOW_PLAN_MAX_CHARS;
/** The words of a refusal for a plan above `FLOW_PLAN_MAX_CHARS`. */
exports.FLOW_PLAN_TOO_LARGE = `The flow plan is too large (at most ${exports.FLOW_PLAN_MAX_CHARS} characters)`;
/** The same refusal as the translation the bar and the Studio show. */
exports.FLOW_PLAN_TOO_LARGE_TEXT = {
    key: 'flowPlanTooLarge',
    params: { limit: exports.FLOW_PLAN_MAX_CHARS },
};
/** Whether the plan is larger than the cell that holds it. */
const flowPlanTooLarge = (plan) => JSON.stringify(plan).length > exports.FLOW_PLAN_MAX_CHARS;
exports.flowPlanTooLarge = flowPlanTooLarge;
/**
 * The plan of an App that is installed under another id: the plan belongs to
 * the App, and an App carries the id of its mount. Only the id changes. The
 * pages the plan names are the routes of the App's definition, and an
 * install changes neither them nor their ids: the route and page mappings of
 * an install (and the route prefix of the mount) rename the pages of the
 * container, not `appDefinition.routes`.
 */
function remapFlowPlan(plan, appId) {
    return { ...plan, appId };
}
/**
 * The plan as a document, or a refusal. Every field is checked as it is read
 * and the document is built from what was checked; a field the plan does not
 * name stays out, the order of the fields stays the one of the file.
 */
function validatePlanningDocument(input, definition) {
    (0, app_flow_check_1.object)(input, [
        'schemaVersion',
        'appId',
        'flows',
        'views',
        'states',
        'provenance',
        'connections',
    ], 'document');
    if (input.schemaVersion !== 2)
        (0, app_flow_check_1.fail)(`schemaVersion is ${String(input.schemaVersion)}; it must be 2`);
    if (input.appId !== definition.appId)
        (0, app_flow_check_1.fail)(`appId is ${String(input.appId)}; it must be the appId of the App, ${definition.appId}`);
    const provenance = input.provenance === undefined
        ? undefined
        : parseProvenance(input.provenance);
    const { flows, views, states, connections } = input;
    list(flows, 64, 'flows');
    list(views, 128, 'views');
    list(states, 256, 'states');
    unique(flows, 'flow');
    unique(views, 'view');
    unique(states, 'state');
    const routes = definition.routes &&
        new Map(definition.routes.map(({ route }) => [route.path, route]));
    const parsedViews = views.map(view => parseView(view, routes));
    const viewById = new Map(parsedViews.map(view => [view.id, view]));
    const parsedStates = states.map(state => parseState(state, viewById));
    const stateById = new Map(parsedStates.map(state => [state.id, state]));
    const parsedFlows = flows.map(flow => parseFlow(flow, { routes, views: viewById, states: stateById }));
    return {
        ...input,
        schemaVersion: 2,
        appId: definition.appId,
        flows: parsedFlows,
        views: parsedViews,
        states: parsedStates,
        ...(provenance ? { provenance } : {}),
        ...(connections === undefined
            ? {}
            : { connections: parseConnections(connections, parsedFlows) }),
    };
}
function parseProvenance(value) {
    (0, app_flow_check_1.object)(value, ['planId', 'version', 'sha256', 'importedAt'], 'provenance');
    const { planId, version, sha256, importedAt } = value;
    if (!(0, app_flow_check_1.text)(planId) ||
        !(0, app_flow_check_1.text)(version) ||
        typeof sha256 !== 'string' ||
        !sha256Pattern.test(sha256) ||
        !isDate(importedAt))
        (0, app_flow_check_1.fail)('invalid provenance');
    return { ...value, planId, version, sha256, importedAt };
}
function parseView(value, routes) {
    (0, app_flow_check_1.object)(value, [
        'id',
        'title',
        'routePath',
        'url',
        'component',
        'interactions',
        'lifecycle',
    ], 'view');
    const { id, title, routePath, url, component, interactions } = value;
    const lifecycle = parseLifecycle(value.lifecycle);
    if (!isId(id))
        (0, app_flow_check_1.fail)('duplicate or invalid view id');
    if (!(0, app_flow_check_1.text)(title))
        (0, app_flow_check_1.fail)('view needs a title');
    if (component !== undefined &&
        (!(0, app_flow_check_1.text)(component, 512) || /[<>\u0000-\u001f]/.test(component)))
        (0, app_flow_check_1.fail)('invalid component reference');
    if (routePath !== undefined &&
        !(typeof routePath === 'string' &&
            (routes === undefined || routes.has(routePath))))
        (0, app_flow_check_1.fail)('view names unknown page');
    const parsedUrl = url === undefined ? undefined : parseUrl(url, routePath);
    list(interactions, 64, 'interactions');
    unique(interactions, 'interaction');
    return {
        ...value,
        id,
        title,
        ...(component === undefined ? {} : { component }),
        ...(routePath === undefined ? {} : { routePath }),
        ...(parsedUrl === undefined ? {} : { url: parsedUrl }),
        interactions: interactions.map(interaction => {
            (0, app_flow_check_1.object)(interaction, ['id', 'selector'], 'interaction');
            const { id: interactionId, selector } = interaction;
            if (!css(selector))
                (0, app_flow_check_1.fail)('invalid interaction selector');
            if (!isId(interactionId))
                (0, app_flow_check_1.fail)('duplicate or invalid interaction id');
            return { ...interaction, id: interactionId, selector };
        }),
        ...(lifecycle ? { lifecycle } : {}),
    };
}
function parseState(value, views) {
    (0, app_flow_check_1.object)(value, [
        'id',
        'title',
        'viewId',
        'url',
        'entryUrl',
        'expect',
        'scenario',
        'prepare',
    ], 'state');
    const { id, title, viewId, url, entryUrl, prepare } = value;
    const view = typeof viewId === 'string' ? views.get(viewId) : undefined;
    if (!isId(id))
        (0, app_flow_check_1.fail)('duplicate or invalid state id');
    if (!view || !(0, app_flow_check_1.text)(title))
        (0, app_flow_check_1.fail)('state names unknown view or missing title');
    const parsedUrl = url === undefined ? undefined : parseUrl(url, view.routePath);
    const parsedEntryUrl = entryUrl === undefined ? undefined : parseUrl(entryUrl, view.routePath);
    const expect = parseExpect(value.expect);
    const scenario = parseScenario(value.scenario);
    return {
        ...value,
        id,
        title,
        viewId: view.id,
        ...(parsedUrl === undefined ? {} : { url: parsedUrl }),
        ...(parsedEntryUrl === undefined ? {} : { entryUrl: parsedEntryUrl }),
        expect,
        scenario,
        ...(prepare === undefined ? {} : { prepare: parsePrepare(prepare) }),
    };
}
function parsePrepare(value) {
    list(value, 4, 'state preparation');
    return value.map(operation => {
        (0, app_flow_check_1.object)(operation, ['type', 'selector', 'fixture', 'checked'], 'state preparation');
        const { type, selector, fixture, checked } = operation;
        if (!oneOf(type, ['upload', 'check', 'click']) ||
            !css(selector) ||
            (fixture !== undefined && fixture !== 'sample-pdf') ||
            (type === 'upload' && fixture !== 'sample-pdf') ||
            (checked !== undefined && typeof checked !== 'boolean') ||
            (type === 'check' && typeof checked !== 'boolean'))
            (0, app_flow_check_1.fail)('invalid state preparation');
        return {
            ...operation,
            type,
            selector,
            ...(fixture === undefined ? {} : { fixture }),
            ...(checked === undefined ? {} : { checked }),
        };
    });
}
function parseFlow(value, context) {
    (0, app_flow_check_1.object)(value, [
        'id',
        'title',
        'description',
        'start',
        'scenario',
        'steps',
        'transitions',
        'evidence',
        'kind',
        'source',
    ], 'flow');
    const { id, title, description, start, kind, source, evidence } = value;
    if (kind !== undefined && !oneOf(kind, ['journey', 'catalogue']))
        (0, app_flow_check_1.fail)('invalid flow kind');
    const parsedSource = source === undefined ? undefined : parseSource(source);
    if (!isId(id))
        (0, app_flow_check_1.fail)('duplicate or invalid flow id');
    if (!(0, app_flow_check_1.text)(title) || !(0, app_flow_check_1.text)(description, 1000))
        (0, app_flow_check_1.fail)('flow needs title and description');
    const { steps, transitions } = value;
    list(steps, 128, 'steps');
    if (!steps.length)
        (0, app_flow_check_1.fail)('flow needs steps');
    const ids = unique(steps, 'step');
    if (typeof start !== 'string' || !ids.has(start))
        (0, app_flow_check_1.fail)('missing start');
    const parsedSteps = steps.map(step => parseStep(step, context));
    list(transitions, 256, 'transitions');
    unique(transitions, 'transition');
    const parsedTransitions = transitions.map(edge => parseTransition(edge, ids, parsedSteps, context));
    const reached = new Set([start]);
    for (const from of reached)
        for (const edge of parsedTransitions)
            if (edge.from === from)
                reached.add(edge.to);
    if (kind !== 'catalogue' && reached.size !== ids.size)
        (0, app_flow_check_1.fail)('every step must be reachable from start');
    if (kind === 'catalogue' && parsedTransitions.length)
        (0, app_flow_check_1.fail)('a state catalogue must not invent a click sequence');
    const scenario = parseScenario(value.scenario);
    return {
        ...value,
        id,
        title,
        description,
        start,
        ...(kind === undefined ? {} : { kind }),
        ...(parsedSource ? { source: parsedSource } : {}),
        scenario,
        steps: parsedSteps,
        transitions: parsedTransitions,
        ...(evidence === undefined ? {} : { evidence: parseEvidence(evidence) }),
    };
}
function parseSource(value) {
    (0, app_flow_check_1.object)(value, ['flowId', 'screenIds', 'copies'], 'source');
    const { flowId, screenIds, copies } = value;
    if (!(0, app_flow_check_1.text)(flowId) ||
        !Array.isArray(screenIds) ||
        screenIds.length > 256 ||
        !screenIds.every(isId) ||
        typeof copies !== 'number' ||
        !Number.isInteger(copies) ||
        copies < 1 ||
        copies > 256)
        (0, app_flow_check_1.fail)('invalid source reference');
    return { ...value, flowId, screenIds, copies };
}
function parseStep(value, context) {
    (0, app_flow_check_1.object)(value, [
        'id',
        'title',
        'intent',
        'viewId',
        'stateId',
        'implementation',
        'actionIds',
        'preview',
        'surface',
        'sourceScreen',
        'gap',
        'lifecycle',
    ], 'step');
    const { id, title, intent, viewId, stateId, actionIds, preview, surface, sourceScreen, gap, } = value;
    const lifecycle = parseLifecycle(value.lifecycle);
    if (surface !== undefined && !oneOf(surface, ['app', 'system']))
        (0, app_flow_check_1.fail)('invalid step surface');
    if (sourceScreen !== undefined && !isId(sourceScreen))
        (0, app_flow_check_1.fail)('invalid screen reference');
    if (gap !== undefined && !(0, app_flow_check_1.text)(gap, 2000))
        (0, app_flow_check_1.fail)('invalid implementation gap');
    if (!(0, app_flow_check_1.text)(title) || (intent !== undefined && !(0, app_flow_check_1.text)(intent, 2000)))
        (0, app_flow_check_1.fail)('invalid step description');
    const view = typeof viewId === 'string' ? context.views.get(viewId) : undefined, state = typeof stateId === 'string' ? context.states.get(stateId) : undefined;
    if ((viewId !== undefined && !view) ||
        (stateId !== undefined && (!state || state.viewId !== viewId)))
        (0, app_flow_check_1.fail)('step names unknown or mismatched view/state');
    (0, app_flow_check_1.object)(value.implementation, ['ui', 'function'], 'implementation');
    const { ui, function: implemented } = value.implementation;
    if (!oneOf(ui, ['planned', 'designed', 'implemented', 'external']) ||
        !oneOf(implemented, ['planned', 'implemented']))
        (0, app_flow_check_1.fail)('invalid implementation status');
    if ((ui === 'external') !== (surface === 'system'))
        (0, app_flow_check_1.fail)('external UI must be an explicit system handoff');
    if (ui !== 'planned' &&
        ui !== 'external' &&
        (!view?.component ||
            !(state?.url ||
                view?.url ||
                view?.routePath?.split('/').some(p => p.startsWith(':')))))
        (0, app_flow_check_1.fail)('designed UI needs an actual component and page binding');
    if (implemented === 'implemented' &&
        surface !== 'system' &&
        (!state?.expect.length || !view?.url || ui !== 'implemented'))
        (0, app_flow_check_1.fail)('implemented function needs implemented UI and observable state');
    // Without the pages of the App the plan cannot be held to their actions.
    const allowed = context.routes &&
        ((view?.routePath && context.routes.get(view.routePath)?.agentActionIds) ||
            []);
    const actions = actionIds === undefined ? undefined : parseActionIds(actionIds, allowed);
    return {
        ...value,
        id: isId(id) ? id : (0, app_flow_check_1.fail)('duplicate or invalid step id'),
        title,
        ...(intent === undefined ? {} : { intent }),
        ...(view ? { viewId: view.id } : {}),
        ...(state ? { stateId: state.id } : {}),
        implementation: { ...value.implementation, ui, function: implemented },
        ...(actions ? { actionIds: actions } : {}),
        ...(preview === undefined ? {} : { preview: parsePreview(preview) }),
        ...(surface === undefined ? {} : { surface }),
        ...(sourceScreen === undefined ? {} : { sourceScreen }),
        ...(gap === undefined ? {} : { gap }),
        ...(lifecycle ? { lifecycle } : {}),
    };
}
/** The Agent Actions a step names, each one the page of its view allows. */
function parseActionIds(value, allowed) {
    const actions = Array.isArray(value)
        ? value.filter((action) => typeof action === 'string' &&
            (allowed === undefined || allowed.includes(action)))
        : [];
    if (!Array.isArray(value) || actions.length !== value.length)
        (0, app_flow_check_1.fail)('step names action its page does not allow');
    return actions;
}
function parsePreview(value) {
    (0, app_flow_check_1.object)(value, ['file', 'revision', 'capturedAt'], 'preview');
    const { file, revision, capturedAt } = value;
    if (!(0, app_flow_check_1.text)(file, 512) ||
        !file.endsWith('.png') ||
        typeof revision !== 'string' ||
        !sha256Pattern.test(revision) ||
        !isDate(capturedAt))
        (0, app_flow_check_1.fail)('invalid captured preview');
    return { ...value, file, revision, capturedAt };
}
function parseTransition(value, ids, steps, context) {
    (0, app_flow_check_1.object)(value, [
        'id',
        'from',
        'to',
        'label',
        'interactionId',
        'selector',
        'navigation',
        'layout',
        'navigationOrder',
    ], 'transition');
    const { id, from, to, label, interactionId, selector } = value;
    const navigation = parseNavigation(value);
    if (typeof from !== 'string' ||
        typeof to !== 'string' ||
        !ids.has(from) ||
        !ids.has(to) ||
        !(0, app_flow_check_1.text)(label))
        (0, app_flow_check_1.fail)('invalid transition endpoint or label');
    const source = steps.find(step => step.id === from);
    const sourceView = source?.viewId === undefined ? undefined : context.views.get(source.viewId);
    if (interactionId !== undefined &&
        !(typeof interactionId === 'string' &&
            sourceView?.interactions.some(item => item.id === interactionId)))
        (0, app_flow_check_1.fail)('unknown source interaction');
    if (selector !== undefined && !css(selector))
        (0, app_flow_check_1.fail)('invalid transition selector');
    if (interactionId && selector)
        (0, app_flow_check_1.fail)('transition must use one control reference');
    return {
        ...value,
        id: isId(id) ? id : (0, app_flow_check_1.fail)('duplicate or invalid transition id'),
        from,
        to,
        label,
        ...(interactionId === undefined ? {} : { interactionId }),
        ...(selector === undefined ? {} : { selector }),
        ...navigation,
    };
}
function parseEvidence(value) {
    (0, app_flow_check_1.object)(value, ['revision', 'checkedAt', 'outcome', 'steps', 'transitions', 'message'], 'evidence');
    const { revision, checkedAt, outcome, steps, transitions, message } = value;
    if (typeof revision !== 'string' ||
        !sha256Pattern.test(revision) ||
        !isDate(checkedAt) ||
        !oneOf(outcome, ['passed', 'failed', 'partial']))
        (0, app_flow_check_1.fail)('invalid evidence');
    list(steps, 128, 'evidence steps');
    list(transitions, 256, 'evidence transitions');
    // A removed step may remain in a historical report; its revision is stale.
    // Keeping that evidence must not prevent editing the plan itself.
    const stepIds = steps.filter(isId), transitionIds = transitions.filter(isId);
    if (stepIds.length !== steps.length ||
        transitionIds.length !== transitions.length ||
        new Set(stepIds).size !== stepIds.length ||
        new Set(transitionIds).size !== transitionIds.length ||
        (message !== undefined && !(0, app_flow_check_1.text)(message, 2000)))
        (0, app_flow_check_1.fail)('invalid historical evidence reference');
    return {
        ...value,
        revision,
        checkedAt,
        outcome,
        steps: stepIds,
        transitions: transitionIds,
        ...(message === undefined ? {} : { message }),
    };
}
function parseConnections(value, flows) {
    list(value, 256, 'connections');
    unique(value, 'connection');
    return value.map(edge => {
        (0, app_flow_check_1.object)(edge, [
            'id',
            'from',
            'to',
            'label',
            'selector',
            'navigation',
            'layout',
            'navigationOrder',
        ], 'connection');
        const { id, label, selector } = edge;
        const navigation = parseNavigation(edge);
        if (selector !== undefined && !css(selector))
            (0, app_flow_check_1.fail)('invalid connection selector');
        const [from, to] = [edge.from, edge.to].map(end => {
            (0, app_flow_check_1.object)(end, ['flowId', 'stepId'], 'connection endpoint');
            const { flowId, stepId } = end;
            if (typeof flowId !== 'string' ||
                typeof stepId !== 'string' ||
                !flows
                    .find(flow => flow.id === flowId)
                    ?.steps.some(step => step.id === stepId))
                (0, app_flow_check_1.fail)('unknown cross-flow endpoint');
            return { ...end, flowId, stepId };
        });
        if (!(0, app_flow_check_1.text)(label))
            (0, app_flow_check_1.fail)('connection needs label');
        return {
            ...edge,
            id: isId(id) ? id : (0, app_flow_check_1.fail)('duplicate or invalid connection id'),
            from,
            to,
            label,
            ...(selector === undefined ? {} : { selector }),
            ...navigation,
        };
    });
}
/** The navigation fields of a transition or connection, which tie the layout and the order to the global role. */
function parseNavigation(edge) {
    const { navigation, layout, navigationOrder } = edge;
    if (navigation !== undefined && !(0, app_flow_types_1.isFlowNavigationRole)(navigation))
        (0, app_flow_check_1.fail)('invalid navigation role');
    if (layout !== undefined && (!(0, app_flow_check_1.text)(layout, 160) || navigation !== 'global'))
        (0, app_flow_check_1.fail)('layout needs global navigation');
    if (navigationOrder !== undefined &&
        (typeof navigationOrder !== 'number' ||
            !Number.isInteger(navigationOrder) ||
            navigationOrder < 0 ||
            navigationOrder > 500 ||
            navigation !== 'global'))
        (0, app_flow_check_1.fail)('invalid navigation order');
    return {
        ...(navigation === undefined ? {} : { navigation }),
        ...(layout === undefined ? {} : { layout }),
        ...(navigationOrder === undefined ? {} : { navigationOrder }),
    };
}
function parseLifecycle(value) {
    if (value === undefined)
        return undefined;
    (0, app_flow_check_1.object)(value, ['role'], 'lifecycle');
    const role = app_flow_lifecycle_1.lifecycleRoles.find(candidate => candidate === value.role);
    if (role === undefined)
        (0, app_flow_check_1.fail)('invalid lifecycle role');
    return { ...value, role };
}
function parseUrl(value, route) {
    if (!(0, app_flow_check_1.text)(value, 2048) || typeof route !== 'string' || !route)
        (0, app_flow_check_1.fail)('URL needs a page binding');
    const url = new URL(value, 'http://streamkit.invalid');
    if (!value.startsWith('/') ||
        value.startsWith('//') ||
        url.origin !== 'http://streamkit.invalid' ||
        !(0, app_base_binding_route_1.appRoutePatternMatches)(route, url.pathname) ||
        url.pathname.startsWith('/__streamkit'))
        (0, app_flow_check_1.fail)('invalid local view URL');
    return value;
}
function parseExpect(items) {
    list(items, 16, 'expectations');
    return items.map(item => {
        (0, app_flow_check_1.object)(item, ['selector', 'checked'], 'expectation');
        const { selector, checked } = item;
        if (!css(selector) ||
            (checked !== undefined && typeof checked !== 'boolean'))
            (0, app_flow_check_1.fail)('invalid expectation');
        return { ...item, selector, ...(checked === undefined ? {} : { checked }) };
    });
}
function parseScenario(items) {
    list(items, 64, 'scenario');
    return items.map(item => {
        (0, app_flow_check_1.object)(item, ['sourceKey', 'rowId', 'values'], 'scenario patch');
        const { sourceKey, rowId } = item;
        if (!isId(sourceKey) || !isId(rowId))
            (0, app_flow_check_1.fail)('invalid scenario reference');
        const raw = item.values;
        (0, app_flow_check_1.object)(raw, Object.keys(raw ?? {}), 'scenario values');
        const values = {};
        const cells = Object.entries(raw);
        if (cells.length > 64)
            (0, app_flow_check_1.fail)('invalid scenario cell');
        for (const [key, cell] of cells) {
            if (!idPattern.test(key) ||
                !(cell === null ||
                    typeof cell === 'boolean' ||
                    (typeof cell === 'number' && Number.isFinite(cell)) ||
                    (typeof cell === 'string' && cell.length <= 10000)))
                (0, app_flow_check_1.fail)('invalid scenario cell');
            values[key] = cell;
        }
        return { ...item, sourceKey, rowId, values };
    });
}
function compileFlowDocument(doc) {
    const views = new Map(doc.views.map(view => [view.id, view]));
    const states = new Map(doc.states.map(state => [state.id, state]));
    return {
        ...doc,
        flows: doc.flows.map(flow => ({
            ...flow,
            steps: flow.steps.map((step) => {
                const view = step.viewId === undefined ? undefined : views.get(step.viewId), state = step.stateId === undefined ? undefined : states.get(step.stateId);
                return {
                    ...step,
                    routePath: view?.routePath,
                    url: state?.url ?? view?.url,
                    ...(state?.entryUrl ? { entryUrl: state.entryUrl } : {}),
                    component: view?.component,
                    ...(step.lifecycle || view?.lifecycle
                        ? { lifecycle: step.lifecycle ?? view?.lifecycle }
                        : {}),
                    expect: state?.expect ?? [],
                    scenario: state?.scenario ?? [],
                    ...(state?.prepare ? { prepare: state.prepare } : {}),
                    canPreview: Boolean((state?.url ?? view?.url) &&
                        step.implementation.ui !== 'planned' &&
                        step.surface !== 'system'),
                    ...(!(state?.url ?? view?.url) &&
                        view?.component &&
                        view?.routePath?.split('/').some(p => p.startsWith(':')) &&
                        step.implementation.ui !== 'planned' &&
                        step.surface !== 'system'
                        ? { needsExample: true }
                        : {}),
                    canRun: step.implementation.function === 'implemented' &&
                        step.surface !== 'system',
                };
            }),
            transitions: flow.transitions.map(edge => {
                const from = flow.steps.find(step => step.id === edge.from);
                return {
                    ...edge,
                    selector: edge.selector ??
                        (from?.viewId === undefined
                            ? undefined
                            : views.get(from.viewId))?.interactions.find(item => item.id === edge.interactionId)
                            ?.selector,
                };
            }),
        })),
    };
}
